import { createApi } from "@reduxjs/toolkit/query/react";
import {
  prepareBaseQuery,
  prepareHeaders,
  prepareHeadersAuthLess,
} from "src/shared/config/storeConfig";
import { searchPropertyBodyParams } from "src/shared/models/containers/account.model";

const hasQueryValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  return (
    normalized !== "" && normalized !== "null" && normalized !== "undefined"
  );
};

export const publicApi = createApi({
  reducerPath: "publicApi",
  baseQuery: prepareBaseQuery(true),
  endpoints: (builder) => ({
    getProperties: builder.mutation({
      query: (abbreviationCode: string) => ({
        url: `public/properties/${abbreviationCode}`,
        method: "GET",
      }),
    }),
    getUnit: builder.query({
      queryFn: async (
        { leaseId, unitID, moveInDate, term, propertyID },
        queryApi,
        extraOptions,
        baseQuery
      ) => {
        const params = new URLSearchParams();
        if (hasQueryValue(moveInDate)) params.set("moveInDate", moveInDate);
        if (hasQueryValue(term)) params.set("term", term);
        if (hasQueryValue(propertyID)) params.set("propertyId", propertyID);
        const query = params.toString();
        const url = !leaseId
          ? `public/units/${unitID}${query ? `?${query}` : ""}`
          : `/v1/lease/${leaseId}`;
        let headers = new Headers();
        headers = leaseId
          ? await prepareHeaders(headers)
          : await prepareHeadersAuthLess(headers);
        return baseQuery({
          url: url,
          method: "GET",
          headers: headers,
        });
      },
    }),
    getUnitData: builder.mutation({
      query: ({ unitID, moveInDate, term, propertyID, leaseId }: any) => {
        const params = new URLSearchParams();
        if (hasQueryValue(moveInDate)) params.set("moveInDate", moveInDate);
        if (hasQueryValue(term)) params.set("term", term);
        if (hasQueryValue(propertyID)) params.set("propertyId", propertyID);
        if (hasQueryValue(leaseId)) params.set("leaseId", leaseId);
        const qs = params.toString();
        return {
          url: `public/units/${unitID}${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
    }),
    getValidateUnitData: builder.mutation({
      query: ({ unitID, propertyID, moveInDate, term }: any) => {
        const params = new URLSearchParams();
        if (hasQueryValue(moveInDate)) params.set("moveInDate", moveInDate);
        if (hasQueryValue(term)) params.set("term", term);
        const qs = params.toString();
        return {
          url: `public/units/validate/${propertyID}/${unitID}${
            qs ? `?${qs}` : ""
          }`,
          method: "GET",
        };
      },
    }),
    getUnits: builder.mutation({
      query: (propertyID: string) => ({
        url: `/public/units/for-propertyId/${propertyID}`,
        method: "GET",
      }),
    }),
    searchProperty: builder.mutation({
      query: (searchPropertyBody: searchPropertyBodyParams) => ({
        url: `/public/validate-resident`,
        method: "POST",
        body: searchPropertyBody,
      }),
    }),
    getAllProperties: builder.mutation({
      query: () => ({
        url: `/public/properties/`,
        method: "GET",
      }),
    }),
    getSinglePropertyDetails: builder.mutation({
      query: (propertyCode) => ({
        url: `/public/properties/${propertyCode}`,
        method: "GET",
      }),
    }),
    searchPropertyForInvitation: builder.mutation({
      query: (invitationId: string) => ({
        url: `/public/validate-resident/for-invitation/${invitationId}`,
        method: "GET",
      }),
    }),
    postIDVerification: builder.mutation({
      query: (body) => ({
        url: `v1/identity-verification`,
        body,
        method: "POST",
      }),
    }),
    getIDVerification: builder.mutation({
      query: (idvId) => ({
        url: `v1/identity-verification/${idvId}`,
        method: "GET",
      }),
    }),
    getCbIdvFailureDetails: builder.mutation({
      query: (idvId) => ({
        url: `v1/identity-verification/${idvId}/failure`,
        method: "GET",
      }),
    }),
    getPlaidIdentitySessionUrl: builder.mutation({
      query: (body) => ({
        url: `v1/identity-verification/application`,
        method: "POST",
        body,
      }),
    }),
    createProspectUser: builder.mutation({
      query: (body) => ({
        url: `v1/lease/prospect`,
        body,
        method: "POST",
      }),
    }),
    getLeasePerson: builder.query({
      query: (args) => ({
        url: `v1/lease/person/${args.id}`,
        method: "GET",
      }),
    }),
    getIncomeLimits: builder.query({
      query: (propertyId: string) => ({
        url: `public/income-limits/${propertyId}`,
        method: "GET",
      }),
    }),
    getPropertyByCode: builder.query({
      query: (propertyCode: string) => ({
        url: `public/properties/${propertyCode}`,
        method: "GET",
      }),
    }),
    getAvailability: builder.query({
      query: (args: {
        propertyCode: string;
        moveInDate: string;
        bedrooms?: number[];
        bathrooms?: number[];
        floorLevel?: number[];
        petsPermitted?: boolean;
        rentMin?: number;
        rentMax?: number;
        sqftMin?: number;
        sqftMax?: number;
        unitType?: string;
        unitNumber?: string;
        page?: number;
        perPage?: number;
      }) => {
        const params = new URLSearchParams();
        params.set("propertyId", args.propertyCode);
        params.set("moveInDate", args.moveInDate);
        if (args.bedrooms?.length)
          params.set("bedrooms", args.bedrooms.join(","));
        if (args.bathrooms?.length)
          params.set("bathrooms", args.bathrooms.join(","));
        if (args.floorLevel?.length)
          params.set("floorLevel", args.floorLevel.join(","));
        if (args.petsPermitted !== undefined)
          params.set("petsPermitted", String(args.petsPermitted));
        if (args.rentMin !== undefined)
          params.set("rentMin", String(args.rentMin));
        if (args.rentMax !== undefined)
          params.set("rentMax", String(args.rentMax));
        if (args.sqftMin !== undefined)
          params.set("sqftMin", String(args.sqftMin));
        if (args.sqftMax !== undefined)
          params.set("sqftMax", String(args.sqftMax));
        if (args.unitType) params.set("unitType", args.unitType);
        if (args.unitNumber) params.set("unitNumber", args.unitNumber);
        if (args.page !== undefined) params.set("page", String(args.page));
        if (args.perPage !== undefined)
          params.set("perPage", String(args.perPage));
        return {
          url: `public/availability/search?${params.toString()}`,
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  useGetPropertiesMutation,
  useGetUnitsMutation,
  useSearchPropertyMutation,
  useGetAllPropertiesMutation,
  useSearchPropertyForInvitationMutation,
  usePostIDVerificationMutation,
  useGetIDVerificationMutation,
  useGetCbIdvFailureDetailsMutation,
  useGetPlaidIdentitySessionUrlMutation,
  useCreateProspectUserMutation,
  useGetUnitQuery,
  useLazyGetLeasePersonQuery,
  useGetUnitDataMutation,
  useGetValidateUnitDataMutation,
  useGetIncomeLimitsQuery,
  useGetAvailabilityQuery,
  useGetPropertyByCodeQuery,
} = publicApi;
